import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { createTheme } from '@mui/material/styles';

import { SidebarFooterProps, ThemeSwitcher } from '@toolpad/core/DashboardLayout';

import { useNoticeCount } from 'contexts/NoticeCountContext';

import DashboardPopup from 'components/layout/DashboardPopup';
import HelpLink from 'components/layout/HelpLink';
import SettingsMenu from 'components/layout/SettingsMenu';
import UserNoticePopup from 'components/layout/UserNoticePopup';
import { ScreenKey } from 'constant/ScreenPermission';
import { UserSession, logoutActiveAccount, switchAccount } from 'stores/AuthenticationStoreAdapter';

export const dashboardTheme = createTheme({
  colorSchemes: {
    light: true,
    dark: {
      palette: {
        background: {
          default: '#06080f',
          paper: '#0b0e18',
        },
      },
    },
  },
  cssVariables: {
    colorSchemeSelector: 'class',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
});

export const toSession = (userSession: UserSession) => {
  return {
    user: {
      id: userSession.userId,
      name: userSession.userName,
      email: userSession.mailAddress,
      roles: userSession.roles,
    },
  };
};

export const signInOut = {
  signIn: () => {},
  /**
   * ログアウト。
   * 現在のアカウントを一覧から除去し、他にログイン済みのアカウントが残っていれば
   * その一番上のアカウントに切り替えてトップへ。残っていなければログインページへ。
   */
  signOut: () => {
    const nextSessionId = logoutActiveAccount();
    if (nextSessionId) {
      // 別のログイン済みアカウントに切り替えて再読み込み
      document.location = '/';
    } else {
      // ログイン済みアカウントが他に無い場合はログインページへ
      document.location = '/login';
    }
  },
};

/**
 * 別のログイン済みアカウントに切り替える。
 * アクティブセッションを切り替えて、アプリを再読み込みし新しいユーザーとして起動する。
 */
export const handleSwitchAccount = (sessionId: string) => {
  switchAccount(sessionId);
  document.location = '/';
};

/**
 * 別のアカウントを追加ログインする。
 * 既存のログイン済みアカウントは保持したままログインページへ遷移する。
 */
export const handleAddAccount = () => {
  document.location = '/login';
};

export function SidebarFooter({ mini }: SidebarFooterProps) {
  return (
    <Typography
      variant="caption"
      sx={{ m: 1, whiteSpace: 'nowrap', overflow: 'hidden', position: 'absolute', bottom: 0 }}
    >
      {mini
        ? `© ${new Date().getFullYear()}`
        : `© ${new Date().getFullYear()} Made with love by Chigira`}
    </Typography>
  );
}

export function NoticeToolbarBadge({
  allowedScreens = [],
  userName = '',
}: {
  allowedScreens?: ScreenKey[];
  userName?: string;
}) {
  const { counts } = useNoticeCount();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [openType, setOpenType] = React.useState<'NOTICE' | 'INFO' | 'IMPORTANT' | null>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>, type: 'NOTICE' | 'INFO' | 'IMPORTANT') => {
    setAnchorEl(e.currentTarget);
    setOpenType(type);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenType(null);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 1 }}>
      {/* 通知アイコン群 */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title="通知">
          <IconButton size="small" onClick={(e) => handleOpen(e, 'NOTICE')}>
            <Badge badgeContent={counts.NOTICE} color="primary" max={99}>
              <NotificationsOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title="お知らせ">
          <IconButton size="small" onClick={(e) => handleOpen(e, 'INFO')}>
            <Badge badgeContent={counts.INFO} color="info" max={99}>
              <InfoOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title="重要なお知らせ">
          <IconButton size="small" onClick={(e) => handleOpen(e, 'IMPORTANT')}>
            <Badge badgeContent={counts.IMPORTANT} color="error" max={99}>
              <NewReleasesOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Badge>
          </IconButton>
        </Tooltip>
      </Stack>
      {openType && (
        <UserNoticePopup
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          noticeType={openType}
        />
      )}
      <Box sx={{ width: 1, height: 24, bgcolor: 'divider', mx: 1 }} />
      <DashboardPopup allowedScreens={allowedScreens} />
      <Box sx={{ width: 1, height: 24, bgcolor: 'divider', mx: 1 }} />
      <ThemeSwitcher />
      <Box sx={{ width: 1, height: 24, bgcolor: 'divider', mx: 1 }} />
      <HelpLink />
      <Box sx={{ width: 1, height: 24, bgcolor: 'divider', mx: 1 }} />
      <SettingsMenu userName={userName} />
    </Stack>
  );
}
